/**
 * Post-Spin View Manager
 * Manages all post-spin related views and state (enjoy overlay, feedback modal, cancellation modal)
 */

import { LitElement, html, css } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';
import { ConfigService } from '../../services/config-service.js';

export interface PostSpinActivity {
  id: string;
  name: string;
  description?: string;
  domain?: string;
  type?: string;
  icon?: string;
  base_challenge_rating?: number;
  duration_range?: string;
  instructions?: string;
  requirements?: string;
  materials?: string;
  activity_tailored_id?: string;
  wheel_item_id?: string;
}

export interface PostActivityFeedbackConfig {
  title: string;
  message: string;
  feedback_type: string;
  content_type: string;
  object_id: string;
  activity: PostSpinActivity | null;
}

@customElement('post-spin-view-manager')
export class PostSpinViewManager extends LitElement {
  static styles = css`
    :host {
      display: block;
    }

    /* Enjoy Overlay Styles */
    .enjoy-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      z-index: 9999;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: var(--color-text-primary);
      text-align: center;
      animation: enjoyOverlayFadeIn 0.5s ease-out;
    }

    @keyframes enjoyOverlayFadeIn {
      0% {
        opacity: 0;
        transform: scale(0.95);
      }
      100% {
        opacity: 1;
        transform: scale(1);
      }
    }

    .enjoy-overlay-content {
      max-width: 600px;
      padding: var(--spacing-8);
      background: rgba(255, 255, 255, 0.9);
      border-radius: var(--radius-2xl);
      border: 1px solid rgba(0, 0, 0, 0.1);
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
      backdrop-filter: blur(10px);
    }

    .enjoy-title {
      font-size: 3rem;
      font-weight: 700;
      margin: 0 0 var(--spacing-6) 0;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }

    .enjoy-activity-info {
      margin-bottom: var(--spacing-6);
    }

    .enjoy-activity-name {
      font-size: 1.5rem;
      font-weight: 600;
      margin-bottom: var(--spacing-3);
      color: var(--color-text-primary);
    }

    .enjoy-activity-description {
      font-size: 1rem;
      color: var(--color-text-secondary);
      line-height: 1.5;
    }

    .enjoy-activity-details {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
      gap: var(--spacing-4);
      margin-bottom: var(--spacing-6);
      padding: var(--spacing-4);
      background: rgba(255, 255, 255, 0.5);
      border-radius: var(--radius-lg);
    }

    .enjoy-detail-item {
      text-align: center;
    }

    .enjoy-detail-label {
      font-size: 0.8rem;
      font-weight: 500;
      color: var(--color-text-secondary);
      margin-bottom: var(--spacing-1);
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .enjoy-detail-value {
      font-size: 1rem;
      font-weight: 600;
      color: var(--color-text-primary);
    }

    .enjoy-hourglass {
      font-size: 2rem;
      margin: var(--spacing-4) 0;
      animation: pulse 2s infinite;
    }

    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }

    .enjoy-timer {
      font-size: 1.2rem;
      font-weight: 600;
      margin-bottom: var(--spacing-4);
      color: var(--color-text-primary);
    }

    .debug-skip-section {
      margin-top: var(--spacing-4);
      padding: var(--spacing-3);
      background: rgba(255, 165, 0, 0.1);
      border: 2px dashed #ff8800;
      border-radius: var(--radius-lg);
    }

    .debug-skip-btn {
      background: #ff8800;
      color: white;
      border: none;
      padding: var(--spacing-2) var(--spacing-4);
      border-radius: var(--radius-md);
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .debug-skip-btn:hover {
      background: #ff6600;
      transform: translateY(-1px);
    }

    .enjoy-close-btn {
      background: var(--color-danger);
      color: white;
      border: none;
      padding: var(--spacing-3) var(--spacing-6);
      border-radius: var(--radius-lg);
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
      margin-top: var(--spacing-4);
    }

    .enjoy-close-btn:hover {
      background: var(--color-danger-dark);
      transform: translateY(-1px);
    }

    /* Modal Overlay */
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      backdrop-filter: blur(4px);
      z-index: 1000;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: var(--spacing-4);
    }

    .modal {
      background: white;
      border-radius: var(--radius-xl);
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
      max-width: 90vw;
      max-height: 90vh;
      overflow-y: auto;
      animation: modalSlideIn 0.3s ease-out;
    }

    @keyframes modalSlideIn {
      0% {
        opacity: 0;
        transform: translateY(-20px) scale(0.95);
      }
      100% {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }

    .modal-header {
      padding: var(--spacing-6);
      border-bottom: 1px solid var(--color-border);
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .modal-title {
      margin: 0;
      font-size: 1.5rem;
      font-weight: 600;
      color: var(--color-text-primary);
    }

    .modal-close {
      background: none;
      border: none;
      font-size: 1.5rem;
      cursor: pointer;
      color: var(--color-text-secondary);
      padding: var(--spacing-2);
      border-radius: var(--radius-md);
      transition: all 0.2s ease;
    }

    .modal-close:hover {
      background: var(--color-background-secondary);
      color: var(--color-text-primary);
    }

    .modal-body {
      padding: var(--spacing-6);
    }

    .modal-actions {
      display: flex;
      gap: var(--spacing-3);
      justify-content: flex-end;
      margin-top: var(--spacing-6);
    }

    .btn-primary {
      background: var(--color-primary);
      color: white;
      border: none;
      padding: var(--spacing-3) var(--spacing-6);
      border-radius: var(--radius-lg);
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .btn-primary:hover {
      background: var(--color-primary-dark);
      transform: translateY(-1px);
    }

    .btn-secondary {
      background: var(--color-background-secondary);
      color: var(--color-text-primary);
      border: 1px solid var(--color-border);
      padding: var(--spacing-3) var(--spacing-6);
      border-radius: var(--radius-lg);
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .btn-secondary:hover {
      background: var(--color-background-tertiary);
      transform: translateY(-1px);
    }

    /* Post-Activity Feedback Modal Styles */
    .post-activity-feedback-modal {
      max-width: 600px;
      width: 90%;
      max-height: 90vh;
      overflow-y: auto;
    }

    .activity-summary {
      margin-bottom: var(--spacing-6);
      padding: var(--spacing-4);
      background: var(--color-background-secondary);
      border-radius: var(--radius-lg);
    }

    .activity-info {
      text-align: center;
    }

    .activity-name {
      font-size: 1.2rem;
      font-weight: 600;
      margin-bottom: var(--spacing-2);
      color: var(--color-text-primary);
    }

    .activity-description {
      color: var(--color-text-secondary);
      line-height: 1.5;
    }

    .feedback-message {
      margin-bottom: var(--spacing-6);
      color: var(--color-text-secondary);
      line-height: 1.5;
    }

    .feedback-form {
      margin-bottom: var(--spacing-6);
    }

    .form-group {
      margin-bottom: var(--spacing-6);
    }

    .form-label {
      display: block;
      margin-bottom: var(--spacing-3);
      font-weight: 600;
      color: var(--color-text-primary);
    }

    .rating-group {
      margin-bottom: var(--spacing-6);
    }

    .rating-options {
      display: flex;
      gap: var(--spacing-3);
      justify-content: center;
      flex-wrap: wrap;
    }

    .rating-option {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: var(--spacing-3);
      border: 2px solid var(--color-border);
      border-radius: var(--radius-lg);
      cursor: pointer;
      transition: all 0.2s ease;
      min-width: 80px;
    }

    .rating-option:hover {
      border-color: var(--color-primary);
      background: var(--color-background-secondary);
    }

    .rating-option input[type="radio"] {
      display: none;
    }

    .rating-option input[type="radio"]:checked + .rating-emoji {
      transform: scale(1.2);
    }

    .rating-option:has(input[type="radio"]:checked) {
      border-color: var(--color-primary);
      background: var(--color-primary-light);
    }

    .rating-emoji {
      font-size: 1.5rem;
      margin-bottom: var(--spacing-1);
      transition: transform 0.2s ease;
    }

    .rating-text {
      font-size: 0.9rem;
      font-weight: 500;
      color: var(--color-text-secondary);
    }

    .form-textarea {
      width: 100%;
      min-height: 100px;
      border: 2px solid var(--color-border);
      border-radius: var(--radius-lg);
      padding: var(--spacing-3);
      font-family: inherit;
      resize: vertical;
      transition: border-color 0.2s ease;
    }

    .form-textarea:focus {
      outline: none;
      border-color: var(--color-primary);
      box-shadow: 0 0 0 3px rgba(var(--color-primary-rgb), 0.1);
    }

    /* Activity Cancellation Modal Styles */
    .cancellation-modal-overlay {
      background: rgba(0, 0, 0, 0.7);
      backdrop-filter: blur(8px);
    }

    .cancellation-modal {
      max-width: 550px;
      width: 90%;
      background: #fff;
      border-radius: var(--radius-xl);
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
    }

    .cancellation-modal .modal-header {
      background: linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%);
      color: white;
      padding: var(--spacing-6);
      border-radius: var(--radius-xl) var(--radius-xl) 0 0;
      text-align: center;
      position: relative;
    }

    .cancellation-icon {
      font-size: 3rem;
      margin-bottom: var(--spacing-3);
      display: block;
    }

    .cancellation-modal .modal-title {
      margin: 0;
      font-size: 1.5rem;
      font-weight: 600;
    }

    .contract-reminder {
      background: #fff3cd;
      border: 1px solid #ffeaa7;
      border-radius: var(--radius-lg);
      padding: var(--spacing-4);
      margin-bottom: var(--spacing-6);
    }

    .contract-reminder-header {
      font-weight: 600;
      color: #856404;
      margin-bottom: var(--spacing-2);
    }

    .contract-reminder-text {
      color: #856404;
      font-size: 0.9rem;
    }

    .activity-reminder {
      margin-bottom: var(--spacing-6);
    }

    .activity-reminder h4 {
      margin: 0 0 var(--spacing-3) 0;
      color: var(--color-text-primary);
    }

    .feedback-section {
      margin-bottom: var(--spacing-6);
    }

    .feedback-section h4 {
      margin: 0 0 var(--spacing-3) 0;
      color: var(--color-text-primary);
    }

    .cancellation-textarea {
      width: 100%;
      min-height: 100px;
      border: 2px solid #e9ecef;
      border-radius: var(--radius-lg);
      padding: var(--spacing-3);
      font-family: inherit;
      resize: vertical;
      transition: border-color 0.2s ease;
    }

    .cancellation-textarea:focus {
      outline: none;
      border-color: #ff6b6b;
      box-shadow: 0 0 0 3px rgba(255, 107, 107, 0.1);
    }

    .cancellation-actions {
      display: flex;
      gap: var(--spacing-3);
      justify-content: space-between;
      margin-top: var(--spacing-6);
    }

    .cancel-cancellation {
      background: var(--color-success);
      color: white;
      border: none;
      padding: var(--spacing-3) var(--spacing-4);
      border-radius: var(--radius-lg);
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
      flex: 1;
    }

    .cancel-cancellation:hover {
      background: var(--color-success-dark);
      transform: translateY(-1px);
    }

    .confirm-cancellation {
      background: #ff6b6b;
      color: white;
      border: none;
      padding: var(--spacing-3) var(--spacing-4);
      border-radius: var(--radius-lg);
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
      flex: 2;
    }

    .confirm-cancellation:hover {
      background: #ee5a52;
      transform: translateY(-1px);
    }

    .btn-tertiary {
      background: var(--color-background-secondary);
      color: var(--color-text-primary);
      border: 1px solid var(--color-border);
    }
  `;

  @property({ type: Object }) activity: PostSpinActivity | null = null;
  @property({ type: String }) view: 'enjoy' | 'feedback' | 'cancellation' | null = null;
  @property({ type: Number }) startTime: number | null = null;

  @state() private enjoyOverlayTimerInterval: number | null = null;

  private configService = ConfigService.getInstance();

  connectedCallback() {
    super.connectedCallback();
    if (this.view === 'enjoy' && this.startTime) {
      this.startEnjoyOverlayTimer();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.clearEnjoyOverlayTimer();
  }

  /**
   * Start live timer updates for enjoy overlay
   */
  private startEnjoyOverlayTimer(): void {
    this.clearEnjoyOverlayTimer();
    this.enjoyOverlayTimerInterval = window.setInterval(() => {
      this.requestUpdate();
    }, 1000);
  }

  /**
   * Clear enjoy overlay timer
   */
  private clearEnjoyOverlayTimer(): void {
    if (this.enjoyOverlayTimerInterval !== null) {
      clearInterval(this.enjoyOverlayTimerInterval);
      this.enjoyOverlayTimerInterval = null;
    }
  }

  render() {
    if (!this.activity || !this.view) return html``;

    switch (this.view) {
      case 'enjoy':
        return this.renderEnjoyOverlay();
      case 'feedback':
        return this.renderPostActivityFeedbackModal();
      case 'cancellation':
        return this.renderActivityCancellationModal();
      default:
        return html``;
    }
  }

  /**
   * Render enjoy overlay
   */
  private renderEnjoyOverlay() {
    if (!this.activity) return html``;

    // Calculate remaining time with enhanced precision for DEBUG mode
    const now = Date.now();
    const startTime = this.startTime || now;
    const elapsedMs = now - startTime;
    const totalMs = 10 * 60 * 1000; // 10 minutes
    const remainingMs = Math.max(0, totalMs - elapsedMs);
    const remainingMinutes = Math.ceil(remainingMs / (60 * 1000));
    const remainingSeconds = Math.ceil((remainingMs % (60 * 1000)) / 1000);
    const isDebugMode = this.configService.isDebugMode();

    return html`
      <div class="enjoy-overlay">
        <div class="enjoy-overlay-content">
          <h1 class="enjoy-title">Enjoy!</h1>

          <div class="enjoy-activity-info">
            <div class="enjoy-activity-name">
              ${this.activity.icon || '🎯'} ${this.activity.name}
            </div>
            <div class="enjoy-activity-description">
              ${this.activity.description || 'No description available'}
            </div>
          </div>

          <div class="enjoy-activity-details">
            <div class="enjoy-detail-item">
              <div class="enjoy-detail-label">Domain</div>
              <div class="enjoy-detail-value">${this.activity.domain || 'General'}</div>
            </div>
            <div class="enjoy-detail-item">
              <div class="enjoy-detail-label">Challenge</div>
              <div class="enjoy-detail-value">${this.activity.base_challenge_rating || 'N/A'}/100</div>
            </div>
            <div class="enjoy-detail-item">
              <div class="enjoy-detail-label">Duration</div>
              <div class="enjoy-detail-value">${this.activity.duration_range || 'Flexible'}</div>
            </div>
            <div class="enjoy-detail-item">
              <div class="enjoy-detail-label">Type</div>
              <div class="enjoy-detail-value">${this.activity.type === 'tailored' ? '✨ Tailored' : '📋 Generic'}</div>
            </div>
          </div>

          ${this.activity.instructions ? html`
            <div class="enjoy-activity-info">
              <div class="enjoy-detail-label">📝 Instructions:</div>
              <div class="enjoy-activity-description">${this.activity.instructions}</div>
            </div>
          ` : ''}

          ${this.activity.requirements ? html`
            <div class="enjoy-activity-info">
              <div class="enjoy-detail-label">📋 Requirements:</div>
              <div class="enjoy-activity-description">${this.activity.requirements}</div>
            </div>
          ` : ''}

          ${this.activity.materials ? html`
            <div class="enjoy-activity-info">
              <div class="enjoy-detail-label">🛠️ Materials:</div>
              <div class="enjoy-activity-description">${this.activity.materials}</div>
            </div>
          ` : ''}

          <div class="enjoy-hourglass">⏳</div>

          <div class="enjoy-timer">
            ${isDebugMode
              ? `${remainingMinutes}:${remainingSeconds.toString().padStart(2, '0')} remaining (DEBUG: ${Math.floor(elapsedMs / 1000)}s elapsed)`
              : `${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''} remaining`
            }
          </div>

          ${isDebugMode ? html`
            <div class="debug-skip-section">
              <div style="margin-bottom: var(--spacing-2); font-size: 0.9rem; color: #ff8800;">
                🐛 DEBUG MODE: Skip timer for testing
              </div>
              <button data-testid="enjoy-skip-timer-btn" class="debug-skip-btn" @click=${this.handleSkipTimer}>
                Skip Timer
              </button>
            </div>
          ` : ''}

          <button data-testid="enjoy-reset-btn" class="enjoy-close-btn" @click=${this.handleResetApp}>
            Reset App
          </button>
        </div>
      </div>
    `;
  }

  private handleSkipTimer() {
    this.dispatchEvent(new CustomEvent('skip-timer', { bubbles: true }));
  }

  private handleResetApp() {
    this.dispatchEvent(new CustomEvent('reset-app', { bubbles: true }));
  }



  /**
   * Render post-activity feedback modal
   */
  private renderPostActivityFeedbackModal() {
    if (!this.activity) return html``;

    return html`
      <div class="modal-overlay" @click=${this.handleCloseFeedback}>
        <div class="modal post-activity-feedback-modal" @click=${(e: Event) => e.stopPropagation()}>
          <div class="modal-header">
            <h3 class="modal-title">How was your experience?</h3>
            <button class="modal-close" data-testid="post-spin-feedback-close-btn" @click=${this.handleCloseFeedback}>×</button>
          </div>
          <div class="modal-body">
            <div class="activity-summary">
              <div class="activity-info">
                <div class="activity-name">
                  ${this.activity.icon || '🎯'} ${this.activity.name}
                </div>
                <div class="activity-description">
                  ${this.activity.description || 'No description available'}
                </div>
              </div>
            </div>

            <p class="feedback-message">Tell us about your experience with "${this.activity.name}". Your feedback helps us improve the system and provide better activity recommendations.</p>

            <div class="feedback-form">
              <!-- Experience Rating -->
              <div class="form-group rating-group">
                <label class="form-label">How was your overall experience?</label>
                <div class="rating-options">
                  <label data-testid="feedback-rating-1" class="rating-option">
                    <input type="radio" name="experience-rating" value="1">
                    <span class="rating-emoji">😞</span>
                    <span class="rating-text">Poor</span>
                  </label>
                  <label data-testid="feedback-rating-2" class="rating-option">
                    <input type="radio" name="experience-rating" value="2">
                    <span class="rating-emoji">😐</span>
                    <span class="rating-text">Fair</span>
                  </label>
                  <label data-testid="feedback-rating-3" class="rating-option">
                    <input type="radio" name="experience-rating" value="3">
                    <span class="rating-emoji">🙂</span>
                    <span class="rating-text">Good</span>
                  </label>
                  <label data-testid="feedback-rating-4" class="rating-option">
                    <input type="radio" name="experience-rating" value="4">
                    <span class="rating-emoji">😊</span>
                    <span class="rating-text">Great</span>
                  </label>
                  <label data-testid="feedback-rating-5" class="rating-option">
                    <input type="radio" name="experience-rating" value="5">
                    <span class="rating-emoji">🤩</span>
                    <span class="rating-text">Amazing</span>
                  </label>
                </div>
              </div>

              <!-- Additional Comments -->
              <div class="form-group">
                <label class="form-label" for="post-activity-feedback-comment">Additional thoughts or suggestions:</label>
                <textarea
                  data-testid="feedback-comment-textarea"
                  id="post-activity-feedback-comment"
                  class="form-textarea"
                  placeholder="Share any additional thoughts, what you learned, or suggestions for improvement..."
                  rows="4"
                ></textarea>
              </div>
            </div>

            <div class="modal-actions">
              <button data-testid="feedback-skip-btn" type="button" class="btn-secondary" @click=${this.handleCloseFeedback}>
                Skip Feedback
              </button>
              <button data-testid="feedback-submit-btn" type="button" class="btn-primary" @click=${this.handleSubmitFeedback}>
                Submit Feedback
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private handleCloseFeedback() {
    this.dispatchEvent(new CustomEvent('close-feedback', { bubbles: true }));
  }

  private handleSubmitFeedback() {
    const experienceRating = this.renderRoot.querySelector('input[name="experience-rating"]:checked') as HTMLInputElement;
    const feedbackTextarea = this.renderRoot.querySelector('#post-activity-feedback-comment') as HTMLTextAreaElement;

    const feedbackData = {
      experienceRating: experienceRating?.value || null,
      comment: feedbackTextarea?.value || '',
      activity: this.activity
    };

    this.dispatchEvent(new CustomEvent('submit-feedback', {
      detail: feedbackData,
      bubbles: true
    }));
  }

  /**
   * Render activity cancellation modal
   */
  private renderActivityCancellationModal() {
    if (!this.activity) return html``;

    return html`
      <div class="modal-overlay cancellation-modal-overlay" @click=${this.handleCloseCancellation}>
        <div class="modal cancellation-modal" @click=${(e: Event) => e.stopPropagation()}>
          <div class="modal-header">
            <div class="cancellation-icon">😔</div>
            <h2 class="modal-title">Breaking Your Commitment</h2>
            <button class="modal-close" data-testid="post-spin-cancellation-close-btn" @click=${this.handleCloseCancellation}>×</button>
          </div>

          <div class="modal-body">
            <div class="contract-reminder">
              <div class="contract-reminder-header">
                <strong>Remember:</strong> You signed a commitment to do this activity.
              </div>
              <div class="contract-reminder-text">
                We understand that sometimes circumstances change, but breaking commitments should be rare.
              </div>
            </div>

            <div class="activity-reminder">
              <h4>The activity you're declining:</h4>
              <div class="activity-info">
                <div class="activity-name">
                  ${this.activity.icon || '🎯'} ${this.activity.name}
                </div>
                <div class="activity-description">
                  ${this.activity.description || 'No description available'}
                </div>
              </div>
            </div>

            <div class="feedback-section">
              <h4>We understand things happen. Please help us understand what went wrong:</h4>
              <textarea
                data-testid="cancellation-reason-textarea"
                id="cancellation-reason"
                class="form-textarea cancellation-textarea"
                placeholder="Please explain what happened that made you unable to do this activity. Your feedback helps us improve the system and provide better recommendations..."
                rows="4"
              ></textarea>
            </div>

            <div class="modal-actions cancellation-actions">
              <button data-testid="cancellation-back-btn" type="button" class="btn-tertiary cancel-cancellation" @click=${this.handleCancelCancellation}>
                Actually, let's do it
              </button>
              <button data-testid="cancellation-confirm-btn" type="button" class="btn-primary confirm-cancellation" @click=${this.handleConfirmCancellation}>
                Submit feedback & reset
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private handleCloseCancellation() {
    this.dispatchEvent(new CustomEvent('close-cancellation', { bubbles: true }));
  }

  private handleCancelCancellation() {
    this.dispatchEvent(new CustomEvent('cancel-cancellation', { bubbles: true }));
  }

  private handleConfirmCancellation() {
    const textarea = this.renderRoot.querySelector('#cancellation-reason') as HTMLTextAreaElement;
    const reason = textarea?.value || '';

    this.dispatchEvent(new CustomEvent('confirm-cancellation', {
      detail: { reason, activity: this.activity },
      bubbles: true
    }));
  }
}
